import { getConversionFactor } from '../../../../utils/conversionUtils.js';
import { validateAndAdjustOfferValue } from '../../../../utils/offerThresholdUtils.js';
import { createOfferContext } from '../../../../utils/offerContextUtils.js';
import { usePersistedDataStore } from '../../../store.js';
import { updateOffersBatch } from './realtimeOfferService.js';

const latestAventineOffer = (negotiations) => {
  if (!Array.isArray(negotiations) || negotiations.length === 0) return null;
  let latest = null;
  for (const negotiation of negotiations) {
    const offer = negotiation?.offers?.find((item) => item.OfferedBy === 'Aventine');
    if (offer && (negotiation.round || 0) >= (latest?.round || 0)) {
      latest = { offer, round: negotiation.round || 0 };
    }
  }
  return latest;
};

const aventineOfferCount = (negotiations) => {
  if (!Array.isArray(negotiations)) return 0;
  return negotiations.reduce(
    (count, negotiation) => count + (negotiation?.offers?.some((item) => item.OfferedBy === 'Aventine') ? 1 : 0),
    0,
  );
};

const workupOfferAV = (row) => {
  const workupMV = Number(row.ProprietyWorkup);
  if (!Number.isFinite(workupMV) || workupMV <= 0) return null;
  try {
    const factor = getConversionFactor(row.EQ, row.RAR);
    const workupAV = factor > 0 ? Math.round(workupMV * factor) : Math.round(workupMV);
    const adjusted = validateAndAdjustOfferValue(workupAV, row, true, false, { inputInAVTerms: true });
    return {
      workupMV,
      workupAV,
      adjustedAV: adjusted.adjustedValue,
      wasAdjusted: adjusted.wasAdjusted,
      adjustmentType: adjusted.adjustmentType,
    };
  } catch (error) {
    return { workupMV, error: error.message };
  }
};

const planSetOfferToWorkup = (row, negotiations, keepManuallySetOffers) => {
  const parcelId = row.parcel_id;
  const address = row.Address;
  if (row.loading || row.ProprietyWorkup === 'Loading...') {
    return { parcelId, address, action: 'skip', reason: 'workup still loading' };
  }
  if (row._isSettled) {
    return { parcelId, address, action: 'skip', reason: 'settled' };
  }

  const latest = latestAventineOffer(negotiations);
  const offer = latest?.offer ?? null;
  const offerSource = offer?.OfferSource ?? null;
  const liveOffer = offer?.LiveOffer ?? null;

  if (keepManuallySetOffers && offerSource === 'Manual') {
    return {
      parcelId,
      address,
      action: 'skip',
      reason: 'OfferSource is Manual and keep manually set offers is on',
      offerSource,
      liveOffer,
      round: latest.round,
      currentOffer: offer.Offer,
      offerId: offer.OfferID,
    };
  }

  const priced = workupOfferAV(row);
  if (!priced || priced.error || priced.adjustedAV == null) {
    return {
      parcelId,
      address,
      action: 'skip',
      reason: priced?.error || 'no workup value',
      offerSource,
      liveOffer,
      round: latest?.round ?? null,
      workupMV: priced?.workupMV ?? row.ProprietyWorkup,
    };
  }

  const shared = {
    parcelId,
    address,
    offerSource,
    liveOffer,
    round: latest?.round ?? null,
    currentOffer: offer?.Offer ?? null,
    offerId: offer?.OfferID ?? null,
    ...(offer && offerSource == null ? { offerKeys: Object.keys(offer) } : {}),
    ...priced,
  };

  if (!offer) {
    return { ...shared, action: 'insert', reason: 'no Aventine offer, insert first offer', apiRound: 1, uiRound: 1 };
  }

  const count = aventineOfferCount(negotiations);
  if (Number(liveOffer) === 1) {
    const maxRound = negotiations.reduce((max, negotiation) => Math.max(max, negotiation?.round || 0), 0);
    return {
      ...shared,
      action: 'insert',
      reason: 'LiveOffer is 1, create a new round',
      apiRound: count + 1,
      uiRound: maxRound + 1,
    };
  }

  if (offer.OfferID == null) {
    return { ...shared, action: 'skip', reason: 'non-live offer has no OfferID' };
  }

  return {
    ...shared,
    action: 'update',
    reason: 'LiveOffer is not 1, update the existing offer',
    apiRound: count,
    uiRound: latest.round,
  };
};

const stampWorkupSource = (written) => {
  const store = usePersistedDataStore.getState();
  const negotiationObj = store.negotiationObj;
  if (!negotiationObj?.cases || written.length === 0) return;
  const byParcel = new Map(written.map((plan) => [plan.parcelId, plan]));
  store.setNegotiationObj({
    ...negotiationObj,
    cases: negotiationObj.cases.map((caseItem) => {
      const plan = byParcel.get(caseItem.parcel_id);
      if (!plan || !Array.isArray(caseItem.Negotiations)) return caseItem;
      return {
        ...caseItem,
        Negotiations: caseItem.Negotiations.map((negotiation) => ({
          ...negotiation,
          offers: (negotiation.offers || []).map((offer) => {
            const matches = plan.action === 'update'
              ? offer.OfferID != null && String(offer.OfferID) === String(plan.offerId)
              : offer.OfferedBy === 'Aventine' && negotiation.round === plan.uiRound;
            return matches ? { ...offer, OfferSource: 'Workup' } : offer;
          }),
        })),
      };
    }),
  });
  store.incrementNegotiationObjVersion();
};

export const applyOffersToWorkup = async ({ rows, negotiationCases, keepManuallySetOffers, user }) => {
  const plans = (rows || []).map((row) => {
    const storeCase = (negotiationCases || []).find((item) => item.parcel_id === row.parcel_id);
    return planSetOfferToWorkup(row, storeCase?.Negotiations || row.Negotiations || [], keepManuallySetOffers);
  });

  console.log('[SetOffersToWorkup] start', {
    keepManuallySetOffers,
    rowCount: plans.length,
    skip: plans.filter((plan) => plan.action === 'skip').length,
    update: plans.filter((plan) => plan.action === 'update').length,
    insert: plans.filter((plan) => plan.action === 'insert').length,
  });
  plans.forEach((plan) => console.log('[SetOffersToWorkup]', plan));

  const writes = plans.filter((plan) => plan.action !== 'skip');
  if (writes.length === 0) return { success: true, plans, written: [] };

  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const context = createOfferContext(user, negotiationObj, 'Workup');
  const offers = writes.map((plan) => ({
    parcelId: plan.parcelId,
    offerValue: plan.adjustedAV,
    offeredBy: 'Aventine',
    offerDate: new Date().toISOString(),
    backendRound: plan.apiRound,
    // New rounds are unsent. In-place updates keep the offer's current LiveOffer.
    liveOffer: plan.action === 'insert' ? 0 : (Number(plan.liveOffer) === 1 ? 1 : 0),
    preDeterminedAction: plan.action === 'update'
      ? { action: 'update', offerId: plan.offerId }
      : { action: 'insert', round: plan.uiRound },
  }));
  console.log('[SetOffersToWorkup] sending batch', { count: offers.length, offers });
  const result = await updateOffersBatch(offers, context, { liveOffer: 0 });
  console.log('[SetOffersToWorkup] batch result', result);
  if (!result?.success) {
    return { success: false, plans, written: [], error: result?.error || 'Failed to set offers to workup' };
  }

  stampWorkupSource(writes);
  console.log('[SetOffersToWorkup] done', {
    updated: writes.filter((plan) => plan.action === 'update').length,
    inserted: writes.filter((plan) => plan.action === 'insert').length,
  });
  return { success: true, plans, written: writes };
};
